import { useState, useMemo, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, Send, Paperclip, Loader2, Forward, X, User as UserIcon, MessageSquare, FileText, Upload } from 'lucide-react'
import {
  useTicket, useReplyTicket, useUpdateTicketStatus,
  useInternalUsers, useForwardTicket,
} from '@/hooks/useNaiDesk'
import { sanitizeHtml, looksLikeHtml } from '@/lib/sanitize'

export default function TicketDetailAdmin() {
  const { id } = useParams<{ id: string }>()
  const { data: t, isLoading } = useTicket(id)
  const reply = useReplyTicket(id || '')
  const updateStatus = useUpdateTicketStatus()
  const [msg, setMsg] = useState('')
  const [showForward, setShowForward] = useState(false)
  const [replyFiles, setReplyFiles] = useState<File[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Timeline cronológica: abertura (description) + replies em ordem ASC
  const timeline = useMemo(() => {
    if (!t) return []
    const items: TimelineItem[] = [
      {
        kind: 'opening',
        id: 'opening',
        date: t.created_at,
        author: t.user_name || '—',
        author_email: t.user_email || '',
        is_admin: false,
        body: t.description,
        attachments: t.attachments || [],
      },
      ...t.replies.map(r => ({
        kind: 'reply' as const,
        id: r.id,
        date: r.created_at,
        author: r.user_name,
        author_email: '',
        is_admin: r.is_admin,
        body: r.message,
        attachments: r.attachments || [],
      })),
    ]
    return items.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
  }, [t])

  if (isLoading || !t) return <div className="p-6"><Loader2 className="h-5 w-5 animate-spin" /></div>

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <Link to="/nai-desk/admin/tickets" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1 mb-4">
        <ArrowLeft className="h-4 w-4" />Voltar
      </Link>

      {/* HEADER do ticket — meta apenas, sem descrição (vai na timeline) */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 mb-4">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex-1 min-w-0">
            <span className="text-xs font-mono text-gray-500">#{t.ticket_number}</span>
            <h1 className="text-xl font-bold">{t.subject}</h1>
            <p className="text-sm text-gray-500 mt-1">de {t.user_name} ({t.user_email})</p>
            {t.assigned_to && (
              <p className="text-sm text-emerald-700 dark:text-emerald-300 mt-1 inline-flex items-center gap-1">
                <UserIcon className="h-3.5 w-3.5" />
                Atribuído a <strong>{t.assigned_to_name}</strong> ({t.assigned_to_email})
              </p>
            )}
          </div>
          <div className="flex gap-2 shrink-0">
            <button onClick={() => setShowForward(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold">
              <Forward className="h-4 w-4" />Encaminhar
            </button>
            <select value={t.status} onChange={e => updateStatus.mutate({ ticketId: t.id, status: e.target.value })}
              className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm">
              <option value="open">Aberto</option>
              <option value="in_progress">Em Andamento</option>
              <option value="forwarded">Encaminhado</option>
              <option value="waiting_client">Aguardando Cliente</option>
              <option value="resolved">Resolvido</option>
              <option value="closed">Fechado</option>
            </select>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 text-xs">
          <span className="bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">{t.type}</span>
          {t.subtype && <span className="bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">{t.subtype}</span>}
          {t.module && <span className="bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-0.5 rounded">{t.module}</span>}
          <span className="bg-yellow-50 text-yellow-700 px-2 py-0.5 rounded">{t.priority}</span>
          {t.sdp_ticket_id && <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">SDP #{t.sdp_ticket_id}</span>}
        </div>

      </div>

      {/* TIMELINE — abertura + replies em ordem cronológica ascendente */}
      <div className="relative pl-7 mb-6">
        <div className="absolute top-2 bottom-2 left-2 w-px bg-gray-200 dark:bg-gray-700" />
        <ol className="space-y-4">
          {timeline.map((it, idx) => (
            <TimelineEntry key={it.id} item={it} index={idx} />
          ))}
        </ol>
      </div>

      {/* CAMPO DE NOVA RESPOSTA */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4">
        <div className="flex items-center gap-2 mb-2 text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
          <Send className="h-3.5 w-3.5" />Nova resposta
        </div>
        <textarea value={msg} onChange={e => setMsg(e.target.value)} placeholder="Escreva a resposta do suporte..."
          rows={4} className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm" />

        {replyFiles.length > 0 && (
          <ul className="mt-2 flex flex-wrap gap-2">
            {replyFiles.map((f, i) => (
              <li key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 text-xs text-blue-700 dark:text-blue-300">
                <Paperclip className="h-3 w-3" />
                <span className="max-w-[220px] truncate">{f.name}</span>
                <span className="text-gray-400 text-[10px]">{(f.size / 1024).toFixed(0)} KB</span>
                <button onClick={() => setReplyFiles(replyFiles.filter((_, j) => j !== i))}
                  className="ml-1 text-gray-400 hover:text-red-600"><X className="h-3 w-3" /></button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex justify-between items-center mt-3">
          <div>
            <input ref={fileInputRef} type="file" multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.webp,.zip"
              className="hidden"
              onChange={e => {
                const arr = Array.from(e.target.files || [])
                setReplyFiles(prev => [...prev, ...arr])
                if (fileInputRef.current) fileInputRef.current.value = ''
              }} />
            <button type="button" onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
              <Upload className="h-4 w-4" />Anexar
            </button>
          </div>
          <button disabled={!msg.trim() || reply.isPending}
            onClick={async () => {
              await reply.mutateAsync({ message: msg, files: replyFiles })
              setMsg(''); setReplyFiles([])
            }}
            className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2">
            {reply.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}Enviar resposta
          </button>
        </div>
      </div>

      {showForward && id && <ForwardModal ticketId={id} currentAssignee={t.assigned_to ?? null} onClose={() => setShowForward(false)} />}

      <style>{`
        .nd-html { word-break: break-word; }
        .nd-html img { max-width: 100%; height: auto; border-radius: 4px; }
        .nd-html table { border-collapse: collapse; max-width: 100%; }
        .nd-html table td, .nd-html table th { border: 1px solid rgba(0,0,0,0.1); padding: 4px 8px; }
        .nd-html blockquote { border-left: 3px solid #cbd5e1; padding-left: 12px; margin: 8px 0; color: #64748b; }
        .nd-html p { margin: 6px 0; }
        .nd-html a { color: #2563eb; text-decoration: underline; }
        .dark .nd-html a { color: #60a5fa; }
        .nd-sdp-img-placeholder {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 8px 12px; margin: 4px 2px;
          border: 1px dashed #cbd5e1; border-radius: 8px;
          background: #f8fafc; color: #475569 !important;
          text-decoration: none !important; font-size: 12px;
          transition: all 0.15s;
        }
        .nd-sdp-img-placeholder:hover {
          border-color: #2563eb; background: #eff6ff; color: #1e40af !important;
        }
        .dark .nd-sdp-img-placeholder { background: rgba(255,255,255,0.04); border-color: rgba(255,255,255,0.15); color: #94a3b8 !important; }
        .dark .nd-sdp-img-placeholder:hover { background: rgba(96,165,250,0.1); border-color: #60a5fa; color: #bfdbfe !important; }
        .nd-sdp-img-icon { font-size: 14px; }
      `}</style>
    </div>
  )
}

interface TimelineItem {
  kind: 'opening' | 'reply'
  id: string
  date: string
  author: string
  author_email: string
  is_admin: boolean
  body: string
  attachments: { id: string; file_path: string; original_name: string; file_size: number; mime_type: string | null }[]
}

function TimelineEntry({ item, index }: { item: TimelineItem; index: number }) {
  const dotColor =
    item.kind === 'opening' ? 'bg-emerald-500' :
    item.is_admin ? 'bg-blue-500' : 'bg-gray-400'

  const cardBg =
    item.kind === 'opening' ? 'bg-emerald-50 dark:bg-emerald-900/15 border-emerald-200 dark:border-emerald-900/40' :
    item.is_admin ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-900/40' :
    'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800'

  return (
    <li className="relative">
      <span className={`absolute -left-[26px] top-3 w-3 h-3 rounded-full ring-4 ring-white dark:ring-gray-900 ${dotColor}`} />
      <div className={`rounded-lg border p-4 ${cardBg}`}>
        <div className="flex items-center justify-between gap-2 mb-2 text-xs">
          <div className="flex items-center gap-1.5">
            {item.kind === 'opening' ? (
              <>
                <FileText className="h-3.5 w-3.5 text-emerald-700 dark:text-emerald-400" />
                <span className="font-bold text-emerald-700 dark:text-emerald-300 uppercase tracking-wider">Abertura</span>
                <span className="text-gray-400">·</span>
                <span className="font-medium">{item.author}</span>
                {item.author_email && <span className="text-gray-500">({item.author_email})</span>}
              </>
            ) : (
              <>
                <MessageSquare className={`h-3.5 w-3.5 ${item.is_admin ? 'text-blue-700 dark:text-blue-400' : 'text-gray-500'}`} />
                <span className="font-semibold">{item.author}</span>
                {item.is_admin && <span className="text-blue-700 dark:text-blue-300">· suporte</span>}
              </>
            )}
          </div>
          <span className="text-gray-500 font-mono text-[11px]">
            {new Date(item.date).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
        {looksLikeHtml(item.body) ? (
          <div className="text-sm text-gray-700 dark:text-gray-300 nd-html"
               dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.body) }} />
        ) : (
          <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{item.body}</p>
        )}
        {item.attachments.length > 0 && (
          <div className="mt-3 pt-3 border-t border-black/5 dark:border-white/10">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Anexos</div>
            <ul className="flex flex-wrap gap-2">
              {item.attachments.map(a => (
                <li key={a.id}>
                  <a href={a.file_path} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/70 dark:bg-gray-800/70 border border-gray-200 dark:border-gray-700 text-xs text-blue-700 dark:text-blue-300 hover:border-blue-400 transition-colors">
                    <Paperclip className="h-3 w-3" />
                    <span className="max-w-[220px] truncate">{a.original_name}</span>
                    <span className="text-gray-400 text-[10px]">{(a.file_size / 1024).toFixed(0)} KB</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </li>
  )
}

function ForwardModal({ ticketId, currentAssignee, onClose }:
  { ticketId: string; currentAssignee: string | null; onClose: () => void }) {
  const { data: users, isLoading } = useInternalUsers()
  const forward = useForwardTicket()
  const [selected, setSelected] = useState<string>(currentAssignee || '')
  const [note, setNote] = useState('')

  const submit = async () => {
    if (!selected) return
    await forward.mutateAsync({ ticketId, assignedTo: selected, message: note || undefined })
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
        <header className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-lg flex items-center gap-2"><Forward className="h-5 w-5" />Encaminhar chamado</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="h-5 w-5" /></button>
        </header>

        <p className="text-xs text-gray-500 mb-3">Apenas usuários NAI internos (@nai-it.com / @nai-it.com.br) ativos.</p>

        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-1">Responsável</label>
        {isLoading ? (
          <div className="text-sm text-gray-500 inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Carregando...</div>
        ) : (
          <select value={selected} onChange={e => setSelected(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm mb-3">
            <option value="">— selecione —</option>
            {(users || []).map(u => (
              <option key={u.id} value={u.id}>{u.full_name} · {u.email}</option>
            ))}
          </select>
        )}

        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-1">Mensagem (opcional)</label>
        <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
          placeholder="Contexto adicional para o responsável..."
          className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm" />

        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-2 text-sm">Cancelar</button>
          <button onClick={submit} disabled={!selected || forward.isPending}
            className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2">
            {forward.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Forward className="h-4 w-4" />}Encaminhar
          </button>
        </div>
      </div>
    </div>
  )
}
